import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getPortfolio } from "../lib/api";
import { TrendingUp, Sparkles, Clock, Coins } from "lucide-react";

const money = (n) =>
  n === null || n === undefined
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPortfolio().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Shell><Skeleton /></Shell>;
  if (!data) return <Shell><div className="text-white/60">Couldn't load portfolio.</div></Shell>;

  const { totals = {}, collections = [], top_value_cards = [], recent_cards = [] } = data;

  return (
    <Shell>
      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl sm:text-5xl font-light tracking-tighter text-white mb-2"
      >
        Your portfolio
      </motion.h1>
      <p className="text-sm text-white/50 mb-8">Total value is recomputed every time you land here, cached 24 h.</p>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
        <StatTile label="Total value" value={money(totals.total_value)} icon={Coins} testid="tile-total-value" />
        <StatTile label="Total cards" value={totals.total_cards?.toLocaleString() || 0} icon={Sparkles} testid="tile-total-cards" />
        <StatTile label="Unique cards" value={totals.unique_cards?.toLocaleString() || 0} icon={TrendingUp} testid="tile-unique-cards" />
      </div>

      {/* Collections grid */}
      <h2 className="text-xs tracking-[0.2em] text-amber-200/60 mb-3">COLLECTIONS</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {collections.map((c) => (
          <Link key={c.id} to={`/collections/${c.id}`} data-testid={`collection-card-${c.id}`} className="group">
            <CollectionCardTile c={c} />
          </Link>
        ))}
      </div>

      {/* Most valuable */}
      {top_value_cards.length > 0 && (
        <>
          <h2 className="text-xs tracking-[0.2em] text-amber-200/60 mb-3">MOST VALUABLE</h2>
          <ul className="glass rounded-2xl divide-y divide-white/5 mb-10" data-testid="top-value-list">
            {top_value_cards.map((c, i) => (
              <RowTile key={i} card={c} />
            ))}
          </ul>
        </>
      )}

      {/* Recent */}
      {recent_cards.length > 0 && (
        <>
          <h2 className="text-xs tracking-[0.2em] text-amber-200/60 mb-3">
            <Clock className="w-3 h-3 inline mr-1" /> RECENTLY ADDED
          </h2>
          <ul className="glass rounded-2xl divide-y divide-white/5" data-testid="recent-list">
            {recent_cards.map((c, i) => (
              <RowTile key={i} card={c} />
            ))}
          </ul>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 sm:py-10">{children}</div>;
}

function Skeleton() {
  return <div className="space-y-4 animate-pulse"><div className="h-10 bg-white/5 rounded"/><div className="h-24 bg-white/5 rounded"/><div className="h-24 bg-white/5 rounded"/></div>;
}

function StatTile({ label, value, icon: Icon, testid }) {
  return (
    <div className="glass rounded-2xl p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] text-amber-200/60 uppercase mb-1.5">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="text-2xl sm:text-3xl font-light tracking-tight text-white">{value}</div>
    </div>
  );
}

function CollectionCardTile({ c }) {
  return (
    <div className="glass rounded-2xl p-5 hover:border-amber-300/40 hover:bg-amber-400/[0.04] transition-colors border border-transparent">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.18em] text-amber-200/60 uppercase">{c.type}</div>
          <div className="text-lg text-white mt-0.5">{c.name}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-wider text-white/40 uppercase">Value</div>
          <div className="text-sm text-amber-200 font-mono">{money(c.total_value)}</div>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-white/55">
        <span>{c.card_count || 0} cards · {c.unique_count || 0} unique</span>
      </div>
      {c.top_thumbs?.length > 0 && (
        <div className="mt-4 flex gap-2">
          {c.top_thumbs.slice(0, 3).map((t, i) => (
            <img key={i} src={t} alt="" className="w-10 h-14 rounded object-cover border border-white/10" />
          ))}
        </div>
      )}
    </div>
  );
}

function RowTile({ card }) {
  return (
    <li className="flex items-center gap-3 p-3">
      <img
        src={card.thumbnail_url || "https://placehold.co/40x56/1a1a1f/ffffff?text=?"}
        alt=""
        className="w-10 h-14 rounded object-cover border border-white/10 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">{card.name || "—"}</div>
        <div className="text-xs text-white/50 truncate">
          {[card.set_code, card.card_number, card.variant !== "Normal" && card.variant].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="text-sm text-amber-200 font-mono">{money(card.price_mid)}</div>
      {card.quantity > 1 && <div className="text-xs text-white/40 w-8 text-right">×{card.quantity}</div>}
    </li>
  );
}
